// Your in-game name as an Indian number plate (the high-security kind: white, black letters, the blue IND strip; its
// hologram wheel left out on purpose, since the Ashoka Chakra can't be used in business), its letters flipping in
// like a split-flap board as you type. And the live check that a name is free (useNameCheck, used by the name pass).
import { useEffect, useRef, useState } from 'react';
import { HANDLE_MAX, checkHandle } from '../../shared/handle';

const FLAP = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

/** one character that rattles through a few others before it lands */
function Flap({ ch, delay }: { ch: string; delay: number }) {
  const [shown, setShown] = useState(ch);
  const first = useRef(true);
  useEffect(() => {
    if (first.current || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      first.current = false;
      setShown(ch);
      return;
    }
    let n = 0;
    let t = window.setTimeout(function step() {
      if (n++ < 5) {
        setShown(FLAP[Math.floor(Math.random() * FLAP.length)]);
        t = window.setTimeout(step, 38);
      } else setShown(ch);
    }, delay);
    return () => clearTimeout(t);
  }, [ch, delay]);
  return <span className={`flap${shown !== ch ? ' spin' : ''}`}>{shown === ' ' ? ' ' : shown}</span>;
}

export function Plate({ name, state, size = 'md' }: { name: string; state?: 'ok' | 'bad' | 'wait' | null; size?: 'sm' | 'md' | 'lg' }) {
  const text = (name || 'YOURNAME').toUpperCase().slice(0, HANDLE_MAX);
  return (
    <div className={`gx-plate ${size}${state ? ` ${state}` : ''}${name ? '' : ' empty'}`} aria-label={`Number plate: ${name || 'your name'}`}>
      <div className="ind" aria-hidden>
        <b>IND</b>
      </div>
      <div className="chars" aria-hidden>
        {text.split('').map((c, i) => (
          <Flap key={i} ch={c} delay={i * 22} />
        ))}
      </div>
      <i className="holo" aria-hidden />
    </div>
  );
}

export type NameState = { status: 'idle' | 'checking' | 'ok' | 'bad'; message: string };

/** Is `name` free? Asked of the server a moment after you stop typing (the rules are checked here first). */
export function useNameCheck(name: string, email?: string): NameState {
  const [s, setS] = useState<NameState>({ status: 'idle', message: '' });
  useEffect(() => {
    if (!name) return setS({ status: 'idle', message: '' });
    const local = checkHandle(name);
    if (!local.ok) return setS({ status: 'bad', message: local.error });
    setS({ status: 'checking', message: 'Checking the register…' });
    const ctl = new AbortController();
    const t = setTimeout(() => {
      fetch(`/api/register/check?name=${encodeURIComponent(name)}${email ? `&email=${encodeURIComponent(email)}` : ''}`, { signal: ctl.signal })
        .then((r) => r.json())
        .then((j: { available: boolean; error?: string }) => setS(j.available ? { status: 'ok', message: 'Available. It can be yours.' } : { status: 'bad', message: j.error ?? 'Not available.' }))
        .catch((e) => e.name !== 'AbortError' && setS({ status: 'idle', message: 'Couldn’t check just now.' }));
    }, 350);
    return () => {
      clearTimeout(t);
      ctl.abort();
    };
  }, [name, email]);
  return s;
}

/** the name box: type, see the plate, see whether it's free */
export function NameField({ value, onChange, check, autoFocus, id = 'gx-name' }: { value: string; onChange: (v: string) => void; check: NameState; autoFocus?: boolean; id?: string }) {
  return (
    <div className="gx-namefield" onClick={(e) => (e.target as HTMLElement).closest('.gx-plate') && document.getElementById(id)?.focus()}>
      <Plate name={value} state={check.status === 'ok' ? 'ok' : check.status === 'bad' ? 'bad' : check.status === 'checking' ? 'wait' : null} size="lg" />
      <label htmlFor={id} className="sr">
        Your in-game name
      </label>
      <input
        id={id}
        className="gx-input mono"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\s/g, '').slice(0, HANDLE_MAX))}
        placeholder="Type your in-game name"
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        maxLength={HANDLE_MAX}
        autoFocus={autoFocus}
      />
      <p className={`gx-namestat ${check.status}`} role="status" aria-live="polite">
        <i aria-hidden />
        {check.message || `3–${HANDLE_MAX} characters: letters, numbers, _ or .`}
      </p>
    </div>
  );
}
